import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Sparkles, CalendarDays, ClipboardList, Layers, LayoutList, Home, FolderOpen, AlertTriangle, Clock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatApiError } from "../lib/format";
import { DIVISIONS, CONTENT_STATUSES, CONTENT_STATUS_LABEL, CONTENT_PLATFORMS, CONTENT_TYPES } from "../lib/constants";
import ContentDetailModal from "../components/ContentDetailModal";

const PIPELINE_STAGES = ["Idea", "Planned", "Production", "Editing", "Waiting Approval", "Scheduled", "Published"];
const STAGE_COLOR = {
  Idea: "bg-zinc-100 text-zinc-700 border-zinc-200",
  Planned: "bg-sky-50 text-sky-700 border-sky-200",
  Production: "bg-amber-50 text-amber-800 border-amber-200",
  Editing: "bg-purple-50 text-purple-800 border-purple-200",
  "Waiting Approval": "bg-orange-50 text-orange-800 border-orange-200",
  Scheduled: "bg-indigo-50 text-indigo-800 border-indigo-200",
  Published: "bg-emerald-50 text-emerald-800 border-emerald-200",
};
const emptyIdea = { title: "", division_id: "", content_type: "Reels", platform: "Instagram", planned_date: "", planned_time: "10:00", idea_summary: "", content_pillar: "", target_audience: "", status: "Idea" };

export default function Content() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState([]);
  const [ideaOpen, setIdeaOpen] = useState(false);
  const [form, setForm] = useState(emptyIdea);
  const [filter, setFilter] = useState({ status: "all", division: "all", platform: "all", q: "" });
  const [detailId, setDetailId] = useState(null);

  const load = () => Promise.all([
    api.get("/content").then(({ data }) => setRows(data)),
    api.get("/content/stats").then(({ data }) => setStats(data)).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    if (filter.status !== "all" && r.status !== filter.status) return false;
    if (filter.division !== "all" && r.division_id !== filter.division) return false;
    if (filter.platform !== "all" && r.platform !== filter.platform) return false;
    if (filter.q) {
      const s = filter.q.toLowerCase();
      if (!(r.title || "").toLowerCase().includes(s) && !(r.objective || "").toLowerCase().includes(s)) return false;
    }
    return true;
  }), [rows, filter]);

  const today = new Date().toISOString().slice(0, 10);
  const my = filtered.filter((r) => r.assigned_creator_id === user?.id);
  const beranda = {
    today_due: filtered.filter((r) => (r.deadline || r.planned_date) === today && r.status !== "Published"),
    awaiting_approval: filtered.filter((r) => r.status === "Waiting Approval"),
    revisions: filtered.filter((r) => r.status === "Editing" && (r.revisions || []).length > 0),
    overdue: filtered.filter((r) => (r.deadline || r.planned_date || "") < today && r.status !== "Published"),
    scheduled_next_7: filtered.filter((r) => r.status === "Scheduled" && r.planned_date >= today),
  };

  const submitIdea = async () => {
    try {
      await api.post("/content", form);
      toast.success("Ide ditambahkan"); setIdeaOpen(false); setForm(emptyIdea); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const runAi = async () => {
    try { const { data } = await api.post("/content/ai-analysis", {}); setInsights(data.insights); toast.success("Analisis AI diperbarui"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openDetail = (id) => setDetailId(id);

  return (
    <div className="space-y-5" data-testid="content-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Ruang Kerja Konten</h1>
          <p className="text-sm text-[#5C6066] mt-1">Bank ide, brief, pipeline, review, publikasi, dan metrik.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runAi} data-testid="btn-ai-analysis"><Sparkles className="w-4 h-4 mr-1.5" /> Analisis AI</Button>
          <Dialog open={ideaOpen} onOpenChange={setIdeaOpen}>
            <DialogTrigger asChild><Button className="bg-brand text-white" data-testid="btn-add-idea"><Plus className="w-4 h-4 mr-1.5" /> Ide Baru</Button></DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle>Tambah Ide Konten</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 py-2">
                <div className="col-span-2"><Label>Judul</Label><Input className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="idea-title" /></div>
                <div><Label>Divisi / Brand</Label>
                  <Select value={form.division_id} onValueChange={(v) => setForm({ ...form, division_id: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="idea-division"><SelectValue placeholder="Pilih" /></SelectTrigger>
                    <SelectContent>{DIVISIONS.map((d) => <SelectItem key={d.id} value={d.id}>{d.short}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Pilar Konten</Label><Input className="mt-1.5" placeholder="Portofolio / Edukasi" value={form.content_pillar} onChange={(e) => setForm({ ...form, content_pillar: e.target.value })}/></div>
                <div><Label>Tipe</Label>
                  <Select value={form.content_type} onValueChange={(v) => setForm({ ...form, content_type: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue/></SelectTrigger>
                    <SelectContent>{CONTENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Platform</Label>
                  <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue/></SelectTrigger>
                    <SelectContent>{CONTENT_PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Rencana Tayang</Label><Input type="date" className="mt-1.5" value={form.planned_date} onChange={(e) => setForm({ ...form, planned_date: e.target.value })}/></div>
                <div className="col-span-2"><Label>Ringkasan Ide</Label><Textarea rows={2} className="mt-1.5" value={form.idea_summary} onChange={(e) => setForm({ ...form, idea_summary: e.target.value })}/></div>
                <div className="col-span-2"><Label>Target Audience</Label><Input className="mt-1.5" placeholder="Bride to be 22-30, Jakarta" value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })}/></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIdeaOpen(false)}>Batal</Button>
                <Button className="bg-brand text-white" onClick={submitIdea} disabled={!form.title || !form.planned_date} data-testid="btn-save-idea">Simpan Ide</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <Input placeholder="Cari judul / objektif…" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} className="w-64 bg-white" data-testid="content-search"/>
        <Select value={filter.division} onValueChange={(v) => setFilter({ ...filter, division: v })}>
          <SelectTrigger className="w-40 bg-white"><SelectValue placeholder="Divisi"/></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Divisi</SelectItem>{DIVISIONS.map((d) => <SelectItem key={d.id} value={d.id}>{d.short}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filter.platform} onValueChange={(v) => setFilter({ ...filter, platform: v })}>
          <SelectTrigger className="w-40 bg-white"><SelectValue placeholder="Platform"/></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Platform</SelectItem>{CONTENT_PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
          <SelectTrigger className="w-44 bg-white"><SelectValue placeholder="Status"/></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Status</SelectItem>{CONTENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{CONTENT_STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="beranda">
        <TabsList className="w-full flex overflow-x-auto justify-start">
          <TabsTrigger value="beranda" data-testid="cc-tab-beranda"><Home className="w-3.5 h-3.5 mr-1"/>Beranda</TabsTrigger>
          <TabsTrigger value="bank" data-testid="cc-tab-bank"><Sparkles className="w-3.5 h-3.5 mr-1"/>Bank Ide</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="cc-tab-pipeline"><Layers className="w-3.5 h-3.5 mr-1"/>Pipeline</TabsTrigger>
          <TabsTrigger value="list" data-testid="cc-tab-list"><LayoutList className="w-3.5 h-3.5 mr-1"/>Daftar</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="cc-tab-calendar"><CalendarDays className="w-3.5 h-3.5 mr-1"/>Kalender</TabsTrigger>
        </TabsList>

        {/* BERANDA */}
        <TabsContent value="beranda" className="pt-4 space-y-4">
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Total Konten" value={stats.total} testId="content-kpi-total" />
              <Kpi label="Terbit" value={stats.published} tone="success" testId="content-kpi-published" />
              <Kpi label="Completion" value={`${stats.completion_pct}%`} testId="content-kpi-pct" />
              <Kpi label="Overdue" value={stats.overdue_count} tone="danger" testId="content-kpi-overdue" />
            </div>
          )}
          {insights.length > 0 && (
            <Card className="p-4 border border-brand/30 bg-[#FFF9F3] shadow-none">
              <div className="flex items-center gap-2 mb-2 text-sm font-semibold"><Sparkles className="w-4 h-4 text-brand" /> Insight AI</div>
              <div className="space-y-2">{insights.map((i, idx) => (
                <div key={idx} className="text-sm p-2 rounded bg-white border border-[#F0EBE0]">
                  <div className="font-medium">{i.title}</div>
                  <div className="text-[#5C6066] text-xs">{i.message}</div>
                </div>))}
              </div>
            </Card>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BerandaPanel title="Menunggu Approval" icon={Clock} tone="amber" items={beranda.awaiting_approval} openDetail={openDetail} testId="panel-awaiting"/>
            <BerandaPanel title="Sedang Revisi" icon={AlertTriangle} tone="orange" items={beranda.revisions} openDetail={openDetail} testId="panel-revisions"/>
            <BerandaPanel title="Deadline Hari Ini" icon={ClipboardList} tone="rose" items={beranda.today_due} openDetail={openDetail} testId="panel-today"/>
            <BerandaPanel title="Overdue" icon={AlertTriangle} tone="rose" items={beranda.overdue} openDetail={openDetail} testId="panel-overdue"/>
            <BerandaPanel title="Terjadwal 7 hari" icon={CalendarDays} tone="indigo" items={beranda.scheduled_next_7} openDetail={openDetail} testId="panel-scheduled"/>
            {user?.role === "content_creator" && (
              <BerandaPanel title="Tugas Saya" icon={ShieldCheck} tone="emerald" items={my.filter((r) => r.status !== "Published").slice(0, 8)} openDetail={openDetail} testId="panel-mine"/>
            )}
          </div>
        </TabsContent>

        {/* BANK IDE */}
        <TabsContent value="bank" className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.filter((r) => r.status === "Idea").map((r) => {
              const div = DIVISIONS.find((d) => d.id === r.division_id);
              return (
                <button key={r.id} onClick={() => openDetail(r.id)} className="text-left border border-[#EAE3D6] rounded-md p-3 bg-white hover:shadow transition" data-testid={`idea-card-${r.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    {div && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${div.color}`}>{div.short}</span>}
                    <span className="text-[10px] text-[#8C9096]">{r.platform} • {r.content_type}</span>
                  </div>
                  <div className="font-medium">{r.title}</div>
                  <div className="text-xs text-[#5C6066] mt-1 line-clamp-2">{r.idea_summary || r.objective || "—"}</div>
                  <div className="text-[11px] text-[#8C9096] mt-2">{r.content_pillar || "Belum berpilar"}{r.planned_date && ` • ${formatDate(r.planned_date)}`}</div>
                </button>
              );
            })}
            {filtered.filter((r) => r.status === "Idea").length === 0 && (
              <Card className="col-span-full p-6 text-center text-sm text-[#8C9096] bg-white border-dashed border-2 border-[#EAE3D6] shadow-none">Belum ada ide. Klik <b>+ Ide Baru</b> untuk memulai.</Card>
            )}
          </div>
        </TabsContent>

        {/* PIPELINE KANBAN */}
        <TabsContent value="pipeline" className="pt-4">
          <div className="overflow-x-auto pb-3">
            <div className="flex gap-3 min-w-[900px]">
              {PIPELINE_STAGES.map((stage) => {
                const items = filtered.filter((r) => r.status === stage);
                return (
                  <div key={stage} className="w-64 flex-shrink-0" data-testid={`kanban-column-${stage.replace(/\s+/g,'-').toLowerCase()}`}>
                    <div className={`text-[11px] uppercase font-semibold px-2 py-1.5 rounded-t border-b-2 ${STAGE_COLOR[stage]}`}>
                      {CONTENT_STATUS_LABEL[stage] || stage} <span className="ml-1 opacity-70">{items.length}</span>
                    </div>
                    <div className="bg-[#FAF6EE]/60 rounded-b p-2 space-y-2 min-h-[400px]">
                      {items.map((r) => {
                        const div = DIVISIONS.find((d) => d.id === r.division_id);
                        return (
                          <button key={r.id} onClick={() => openDetail(r.id)} className="w-full text-left bg-white rounded p-2 border border-[#EAE3D6] hover:border-brand transition" data-testid={`kanban-card-${r.id}`}>
                            <div className="flex items-center justify-between gap-2">
                              {div && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${div.color}`}>{div.short}</span>}
                              <span className="text-[10px] text-[#8C9096]">{r.platform}</span>
                            </div>
                            <div className="text-sm font-medium mt-1 line-clamp-2">{r.title}</div>
                            <div className="text-[10px] text-[#8C9096] mt-1">
                              {r.creator_name || "Belum di-assign"}
                              {r.deadline && ` • DL ${formatDate(r.deadline)}`}
                            </div>
                            {(r.revisions || []).length > 0 && <div className="text-[10px] text-amber-700 mt-1">{r.revisions.length} revisi</div>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* LIST */}
        <TabsContent value="list" className="pt-4">
          <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                    <th className="text-left px-4 py-3">Judul</th>
                    <th className="text-left px-4 py-3">Divisi</th>
                    <th className="text-left px-4 py-3">Platform / Tipe</th>
                    <th className="text-left px-4 py-3">Tayang</th>
                    <th className="text-left px-4 py-3">CC</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8C9096]">Belum ada konten.</td></tr>}
                  {filtered.map((r) => {
                    const div = DIVISIONS.find((d) => d.id === r.division_id);
                    return (
                      <tr key={r.id} className="border-t border-[#F0EBE0] hover:bg-[#F7F4EE]" data-testid={`content-row-${r.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium">{r.title}</div>
                          <div className="text-[11px] text-[#8C9096]">{r.objective || r.idea_summary || "-"}</div>
                        </td>
                        <td className="px-4 py-3">{div && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${div.color}`}>{div.short}</span>}</td>
                        <td className="px-4 py-3 text-[#5C6066]"><div>{r.platform}</div><div className="text-[10px]">{r.content_type}</div></td>
                        <td className="px-4 py-3">{formatDate(r.planned_date)}</td>
                        <td className="px-4 py-3 text-[#5C6066]">{r.creator_name || "-"}</td>
                        <td className="px-4 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${STAGE_COLOR[r.status] || "bg-zinc-100"}`}>{CONTENT_STATUS_LABEL[r.status] || r.status}</span></td>
                        <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => openDetail(r.id)} data-testid={`btn-open-content-${r.id}`}><FolderOpen className="w-3.5 h-3.5 mr-1"/>Buka</Button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* CALENDAR */}
        <TabsContent value="calendar" className="pt-4">
          <ContentCalendar rows={filtered} openDetail={openDetail}/>
        </TabsContent>
      </Tabs>

      <ContentDetailModal contentId={detailId} open={!!detailId} onOpenChange={(v) => { if (!v) setDetailId(null); }} onChanged={load}/>
    </div>
  );
}

const BerandaPanel = ({ title, icon: Icon, tone, items, openDetail, testId }) => {
  const t = { amber: "text-amber-700", orange: "text-orange-700", rose: "text-rose-700", indigo: "text-indigo-700", emerald: "text-emerald-700" }[tone] || "text-[#3C4045]";
  return (
    <Card className="p-3 border border-[#E5E0D8] bg-white shadow-none" data-testid={testId}>
      <div className={`flex items-center gap-2 text-sm font-semibold mb-2 ${t}`}><Icon className="w-4 h-4"/>{title} <span className="text-[11px] font-normal text-[#8C9096]">({items.length})</span></div>
      <div className="space-y-1.5 max-h-52 overflow-y-auto">
        {items.length === 0 && <div className="text-xs italic text-[#8C9096]">Kosong.</div>}
        {items.slice(0, 10).map((r) => {
          const div = DIVISIONS.find((d) => d.id === r.division_id);
          return (
            <button key={r.id} onClick={() => openDetail(r.id)} className="w-full text-left flex items-center gap-2 border border-[#F0EBE0] rounded px-2 py-1.5 hover:bg-[#FAF6EE]" data-testid={`panel-item-${r.id}`}>
              {div && <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${div.color}`}>{div.short}</span>}
              <span className="text-sm truncate flex-1">{r.title}</span>
              <span className="text-[10px] text-[#8C9096]">{r.platform}</span>
            </button>
          );
        })}
      </div>
    </Card>
  );
};

const ContentCalendar = ({ rows, openDetail }) => {
  const [ref, setRef] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const first = new Date(ref.y, ref.m, 1);
  const daysInMonth = new Date(ref.y, ref.m + 1, 0).getDate();
  const startDay = first.getDay();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const byDate = {};
  rows.forEach((r) => { if (!r.planned_date) return; (byDate[r.planned_date] = byDate[r.planned_date] || []).push(r); });
  const monthName = first.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  return (
    <Card className="p-4 border border-[#E5E0D8] bg-white" data-testid="content-calendar-view">
      <div className="flex items-center justify-between mb-3">
        <Button variant="outline" size="sm" onClick={() => setRef(({y,m}) => m === 0 ? {y:y-1, m:11} : {y, m:m-1})}>‹ Sebelumnya</Button>
        <div className="font-display font-semibold text-lg">{monthName}</div>
        <Button variant="outline" size="sm" onClick={() => setRef(({y,m}) => m === 11 ? {y:y+1, m:0} : {y, m:m+1})}>Berikutnya ›</Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase text-[#8C9096] font-semibold mb-1">
        {["Min","Sen","Sel","Rab","Kam","Jum","Sab"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, idx) => {
          if (!d) return <div key={idx} className="h-24 bg-[#FAF6EE]/50 rounded"/>;
          const iso = `${ref.y}-${String(ref.m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
          const items = byDate[iso] || [];
          const isToday = iso === new Date().toISOString().slice(0,10);
          return (
            <div key={idx} className={`h-24 rounded border p-1 overflow-hidden ${isToday ? "border-brand bg-[#FFF9F3]" : "border-[#E5E0D8] bg-white"}`}>
              <div className={`text-[11px] font-semibold mb-0.5 ${isToday ? "text-brand" : "text-[#5C6066]"}`}>{d}</div>
              <div className="space-y-0.5">
                {items.slice(0, 3).map((c) => {
                  const div = DIVISIONS.find((x) => x.id === c.division_id);
                  return <button key={c.id} onClick={() => openDetail(c.id)} className={`w-full text-[9px] px-1 rounded truncate text-left ${div?.color || "bg-zinc-100"}`} title={c.title}>{c.title}</button>;
                })}
                {items.length > 3 && <div className="text-[9px] text-[#8C9096]">+{items.length-3} lagi</div>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

const Kpi = ({ label, value, tone, testId }) => (
  <Card className="p-4 border border-[#E5E0D8] bg-white shadow-none" data-testid={testId}>
    <div className="text-xs uppercase tracking-wider text-[#8C9096] font-semibold">{label}</div>
    <div className={`mt-1.5 font-display text-2xl font-bold ${tone === "danger" ? "text-rose-700" : tone === "success" ? "text-emerald-700" : "text-[#1E2022]"}`}>{value}</div>
  </Card>
);
